import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { subcontractRequestsApi, usersApi, type SubcontractRequest } from '@/lib/apiClient';
import { StateView } from '@/components';
import ContractsStyles from './ContractsStyles';
import SubcontractRequestForm from './SubcontractRequestForm';
import SubcontractRequestDetail from './SubcontractRequestDetail';
import { requestHref } from './subcontractRequestModel';

/**
 * The three pages of a subcontract request (#627), each a route of its own so a request has an address that can be
 * linked to from a notification and reloaded: `/contracts/requests/new/:projectId`, `/contracts/requests/:id` and
 * `/contracts/requests/:id/edit`. They load; the screens are in `SubcontractRequestForm` and `…Detail`.
 *
 * What each one may show is the API's to say: a request the viewer may not know exists, or on a job they may not
 * see, is a 404 here as everywhere, and the page says so in the words the API gave.
 */

/** A positive whole number from the address, or null for anything else (`/new/abc`, `/0`, `/1.5`). */
function idFrom(raw: string | undefined): number | null {
  return raw !== undefined && /^\d+$/.test(raw) && Number(raw) > 0 ? Number(raw) : null;
}

const NOT_FOUND = <div className="ct-hint" role="note">That page isn’t a subcontract request.</div>;

/**
 * The request under its key. Shared, so a save on one page is what the next page opens on.
 *
 * `refetchOnMount: 'always'` is for a page that must not start from the cache: the app's global staleTime is two
 * minutes, so without it a request fetched a moment ago (the page the person came from) is "fresh", no fetch is made,
 * and a page that waits for one (`isFetchedAfterMount`) waits forever.
 */
function useRequest(id: number | null, opts: { alwaysRefetch?: boolean } = {}) {
  return useQuery({
    queryKey: ['contracts', 'request', id],
    queryFn: () => subcontractRequestsApi.get(id!),
    enabled: id !== null,
    ...(opts.alwaysRefetch ? { refetchOnMount: 'always' as const } : {}),
  });
}

/** The job's facts for a request that exists: its template list and what the form starts from. */
function usePrefillFor(request: SubcontractRequest | undefined) {
  return useQuery({
    queryKey: ['contracts', 'request-prefill', request?.projectId],
    queryFn: () => subcontractRequestsApi.prefill(request!.projectId),
    enabled: !!request,
  });
}

/** Start a request on a job. */
export function NewSubcontractRequestPage() {
  const projectId = idFrom(useParams().projectId);
  const prefillQ = useQuery({
    queryKey: ['contracts', 'request-prefill', projectId],
    queryFn: () => subcontractRequestsApi.prefill(projectId!),
    enabled: projectId !== null,
  });
  return (
    <>
      <ContractsStyles />
      {projectId === null ? NOT_FOUND : (
        <StateView query={prefillQ}>
          {(prefill) => <SubcontractRequestForm key={prefill.project.id} prefill={prefill} />}
        </StateView>
      )}
    </>
  );
}

/** Why a request cannot be changed, in the words the API uses for the same refusal. */
function whyNotEditable(request: SubcontractRequest): string {
  if (request.status === 'withdrawn') return 'This request was withdrawn, so it can’t be changed.';
  if (request.status === 'submitted') return 'This request is already being processed, so only the coordinator can change it now.';
  return 'You can’t change this request.';
}

/**
 * Change a request that exists.
 *
 * ⚠️ THE FORM OPENS ON FRESH DATA. It takes its starting values once, when it mounts, and the request is cached from
 * the page the person came from, so showing the cached copy would freeze the form on it and ignore the refetch that
 * follows. The page waits for a SUCCESSFUL fetch made since it mounted: `isFetchedAfterMount` alone is also true after
 * a failed one, and `StateView` shows cached data when a refetch fails, so a failure would have opened the stale form
 * anyway. A failed first fetch is shown as an error. Once the form has opened, a later failed refetch leaves it alone
 * (swapping it for an error would throw away what has been typed).
 *
 * And it opens only for a request the API says may be changed (`access.canEdit`); otherwise it says why, instead of
 * offering forty fields that every Save would refuse.
 */
export function EditSubcontractRequestPage() {
  const id = idFrom(useParams().id);
  const requestQ = useRequest(id, { alwaysRefetch: true });
  // The job's facts are for the form, so they are fetched only for a request that may be changed.
  const prefillQ = usePrefillFor(requestQ.data?.access.canEdit ? requestQ.data : undefined);
  const [openedFor, setOpenedFor] = useState<number | null>(null);
  const freshNow = requestQ.isFetchedAfterMount && requestQ.isSuccess;
  useEffect(() => { if (freshNow) setOpenedFor(id); }, [freshNow, id]);
  const ready = freshNow || openedFor === id;
  const fresh = {
    ...requestQ,
    isPending: requestQ.isPending || (!ready && !requestQ.isError),
    // until a fresh copy has arrived there is no data to show: a failed fetch is an error, not the old copy
    data: ready ? requestQ.data : undefined,
  };
  return (
    <>
      <ContractsStyles />
      {id === null ? NOT_FOUND : (
        <StateView query={fresh}>
          {(request) => (request.access.canEdit ? (
            <StateView query={prefillQ}>
              {(prefill) => <SubcontractRequestForm key={request.id} request={request} prefill={prefill} />}
            </StateView>
          ) : (
            <div className="ct-page">
              <Link className="ct-back" to={requestHref(request.id)}>← Back to the request</Link>
              <div className="ct-hint" role="note">{whyNotEditable(request)}</div>
            </div>
          ))}
        </StateView>
      )}
    </>
  );
}

/** One request, for its requester and for the coordinator. */
export function SubcontractRequestPage() {
  const id = idFrom(useParams().id);
  const requestQ = useRequest(id);
  // Cached app-wide under this key (ProjectWorkspace reads it the same way). Who may type a number and download
  // is the API's rule; this only decides which panel to offer, and the API refuses the rest.
  const meQ = useQuery({ queryKey: ['me'], queryFn: () => usersApi.me(), staleTime: 5 * 60_000 });
  const me = meQ.data;
  const isCoordinator = !!me && (me.roles.includes('Admin') || me.permissions.includes('contracts.edit'));
  return (
    <>
      <ContractsStyles />
      {id === null ? NOT_FOUND : (
        <StateView query={requestQ}>
          {(request) => <SubcontractRequestDetail key={request.id} request={request} isCoordinator={isCoordinator} />}
        </StateView>
      )}
    </>
  );
}
